import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { useGetBomData } from '../hooks';
import { usePricingAndLeadTimeBodList } from '../../pricing&LeadTime/queries';
import { prepareAllBomTableDataFromApis } from '../utils';
import { useGetBomRoundingPresetQuery, useGetBomSectionsQuery, useOptionSectionQuery } from '../../queries';
import { useGetLotDetailsQuery } from '../../pricing&LeadTime/lotPriceModal/queries';
import { ProcessedItem, RoundingBehaviour, SectionAndBomItemData, SectionData, TableDataItem } from '../types';
import { useGetDatasheetTabDataQuery, useGetOmDataQuery } from '../../queries';
import { TAB_VALUES } from '../constants';

export const useBomAllTableData = (bomId: string) => {
    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const tabValue = params?.get('tab');

    const { data: sectionData, isLoading: isSectionLoading } = useGetBomSectionsQuery(Number(bomId), {
        enabled: Boolean(bomId)
    });
    const { data: optionSectionData, isLoading: isOptionsLoading } = useOptionSectionQuery(Number(bomId), {
        enabled: Boolean(bomId)
    });
    const {
        data: bomData,
        isLoading: isBomDataLoading,
        mainProductOptionsInfo,
        isFetching: isFetchingBomData
    } = useGetBomData(bomId, {
        enabled: Boolean(bomId)
    });

    const { data: datasheetTabData, isLoading: isDatasheetLoading } = useGetDatasheetTabDataQuery(bomId, {
        enabled: Boolean(bomId) && tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE
    });

    const { data: omData, isLoading: isOmDataLoading } = useGetOmDataQuery(bomId, {
        enabled: Boolean(bomId) && tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE
    });

    const { isLoading: isRoundingPresetLoading, data: bomRoundingPreset } = useGetBomRoundingPresetQuery(bomId, {
        enabled: Boolean(bomId)
    });
    const { data: pricingBodsList, isLoading: isPricingBodListLoading } = usePricingAndLeadTimeBodList(Number(bomId), { enabled: Boolean(bomId) });
    const { data: lotdata, isFetching: isLotDataFetching, isLoading: isLotDataLoading } = useGetLotDetailsQuery(bomId, { enabled: Boolean(bomId) });

    const isDataLoading = useMemo(
        () =>
            isSectionLoading ||
            isOptionsLoading ||
            isBomDataLoading ||
            isRoundingPresetLoading ||
            isPricingBodListLoading ||
            isLotDataLoading ||
            isFetchingBomData ||
            (tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE ? isDatasheetLoading : false) ||
            (tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE ? isOmDataLoading : false),
        [isBomDataLoading, isDatasheetLoading, isFetchingBomData, isLotDataLoading, isOmDataLoading, isOptionsLoading, isPricingBodListLoading, isRoundingPresetLoading, isSectionLoading, tabValue]
    );

    // step 1 :: Preparing the table data and other meta data considering all the columns accrross all the tabs.
    const {
        tableData,
        totalExtendedPrice = 0,
        totalCost,
        totalExtDiscountedCost,
        totalMarginCost,
        totalMarginPercentage
    } = useMemo(() => {
        return prepareAllBomTableDataFromApis({
            initBodsData: bomData?.data,
            pricingData: pricingBodsList?.data,
            lotsData: lotdata?.data,
            roundingPrecision: bomRoundingPreset?.data.rounding_precision as RoundingBehaviour,
            roundingBehaviourOnCell: bomRoundingPreset?.data?.rounding_behaviour,
            submittalData: tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE ? datasheetTabData?.data : [],
            omData: tabValue === TAB_VALUES.DOCUMENTS_TAB_VALUE ? omData?.data : [],
            mainProductOptionsInfo
        });
    }, [
        bomData?.data,
        pricingBodsList?.data,
        lotdata?.data,
        bomRoundingPreset?.data.rounding_precision,
        bomRoundingPreset?.data?.rounding_behaviour,
        tabValue,
        datasheetTabData?.data,
        omData?.data,
        mainProductOptionsInfo
    ]);

    // step 2 :: Processing the table data into sections & options
    const { section, option }: { section: SectionAndBomItemData[]; option: SectionAndBomItemData[] } = useMemo(() => {
        if (!tableData) {
            return { section: [], option: [] };
        }
        try {
            const kitMapping = new Map(tableData.filter((item) => item.is_kit).map((item) => [item.id, item.id]));
            const sectionDataMap = new Map(sectionData?.data?.map((section) => [section.id, section]));
            const optionSectionDataMap = new Map(optionSectionData?.data?.map((section) => [section.id, section]));
            const processItems = (items: TableDataItem[], sectionData: Map<number, SectionData | undefined>, sectionKey: 'section' | 'substitute_section'): ProcessedItem[] =>
                (items as ProcessedItem[])
                    .map((item) => {
                        const sectionId = item[sectionKey];
                        const section = sectionId !== undefined ? sectionData.get(sectionId) : undefined;
                        if (!section) return null;

                        const kitCategoryName = kitMapping.get(item.kit || 0);
                        return {
                            ...item,
                            sectionName: (section as { name: string })?.name,
                            groupPath: kitCategoryName ? [(section as { id: number }).id, item.kit!, item.id] : [(section as { id: number }).id, item.id]
                        };
                    })
                    .filter((item): item is ProcessedItem => item !== null);

            const sectionTableData = processItems(tableData as TableDataItem[], sectionDataMap as Map<number, SectionData>, 'section');
            const optionTableData = processItems(tableData as TableDataItem[], optionSectionDataMap as Map<number, SectionData>, 'substitute_section');

            const addGroupPathToSections = (sections: SectionData | undefined): SectionAndBomItemData[] =>
                sections?.data?.map((section) => ({
                    ...section,
                    groupPath: [section.id],
                    category: section.name,
                    sectionName: section.name
                })) || [];

            const result = {
                section: [...addGroupPathToSections(sectionData), ...sectionTableData] as SectionAndBomItemData[],
                option: [...optionTableData, ...addGroupPathToSections(optionSectionData)] as SectionAndBomItemData[]
            };

            return result;
        } catch (error) {
            console.error('Error processing table data:', error);
            return { section: [], option: [] };
        }
    }, [tableData, optionSectionData, sectionData]);

    return {
        mainProductOptionsInfo,
        isBomDataLoading,
        isLotDataFetching,
        isLotDataLoading,
        section,
        option,
        totalExtendedPrice,
        totalCost,
        totalExtDiscountedCost,
        totalMarginCost,
        totalMarginPercentage,
        isSectionLoading,
        isOptionsLoading,
        optionsApiData: optionSectionData,
        lotApiData: lotdata,
        unProcessedTableData: tableData,
        isDataLoading,
        currentTabValue: tabValue,
        sectionData
    };
};
